import { AuthState, MeResponse, User } from '@/types/auth';

import { httpRequest } from '@/services/httpRequest';

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const checkAuth = createAsyncThunk<MeResponse, void, { rejectValue: string }>(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await httpRequest({
        url: '/v1/auth/me',
        method: 'GET',
      });
      return data as MeResponse;
    } catch {
      return rejectWithValue('Not authenticated');
    }
  },
);

export const logoutAsync = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logoutAsync',
  async (_, { rejectWithValue }) => {
    try {
      await httpRequest({
        url: '/v1/auth/logout',
        method: 'POST',
      });
    } catch {
      return rejectWithValue('Logout failed');
    }
  },
);

const transformMeResponseToUser = (data: MeResponse): User => ({
  id: data.user_id,
  email: data.email,
  firstName: data.first_name,
  lastName: data.last_name,
  canRefreshCache: data.can_refresh_cache,
  canManageConfiguration: data.can_manage_configuration,
  canManageUsers: data.can_manage_users,
  canManageIncidents: data.can_manage_incidents,
  canManageIdeaRequests: data.can_manage_idea_requests,
  canManagePolicies: data.can_manage_policies,
  canUseMcp: data.can_use_mcp,
  canDeployProduction: data.can_deploy_production,
  canDeployNonproduction: data.can_deploy_nonproduction,
  canDeployInternal: data.can_deploy_internal,
});

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  initialized: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.user = transformMeResponseToUser(action.payload);
        state.isAuthenticated = true;
        state.loading = false;
        state.initialized = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.initialized = true;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
      })
      .addCase(logoutAsync.rejected, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.loading = false;
      });
  },
});

export const { setLoading, setError, clearError, logout } = authSlice.actions;
export default authSlice.reducer;
